/**
 * The finding drawer, sheet 21 (mission 6 WP08 T047; FR-013, FR-015, FR-027;
 * C-009, C-013; ANA.2, ANA.4, ANA.6, ANA.9, GL.2, TEN.10, CMP.6; style guide
 * §7.11; research §1.10, §1.18; post-plan C16; post-tasks P6, P20;
 * DM-01M39PR6WCD3792M8TVM59WYJ0).
 *
 * The 520 px `Sheet`, opened by `?rule=` (and `&severity=` for one segment).
 * The static explanation is the first answer, with no model call.
 * - Header: the rule id (`h3`, mono) and the severity badge, from the loaded
 *   by-rule rows; when the rule is not on the page, from `['rule', id]` and
 *   `rule_counts[id]` (C16). A bare `?rule=` with two segments lists both; off
 *   the page its severities come from the rule's truncated segments and its
 *   first records page, the same read the records table makes, so the badges
 *   and the ANA.4 lines render there too (mission 8 WP09 T047; FR-020 A-L3).
 * - Body: the facts, the ANA.4 line verbatim with the attribution and the
 *   legend, `RuleExplanation`, and the affected records in pages of 50. A
 *   value renders only when the API marked it `available`; otherwise register
 *   9 says why (TEN.10). Nothing is derived from record bytes, and no value
 *   or record reference is ever an attribute. A segment with no listed rows
 *   (`in_window: 0`) sends no records request and points at the export.
 * - Footer: "View in rule catalog" and `assistantAskAboutRule`, the hand-off
 *   to the docked assistant (mission 8 WP09 T047; FR-013, FR-025; SAT.1; 21/7;
 *   OR-10, TR-6, E5; squad U1, U15). It calls `openPanel` once with
 *   `replace`, the rule, the prefill `{kind: 'rule', ruleId, count}` (`count`
 *   is `rule_counts[rule]`, the rule's total over its segments) and
 *   `drawerCloseEdit`: one replacing navigation closes the drawer, restores the
 *   held table filter and opens the panel, so Back returns to the results
 *   without the drawer. No feedback control (C-009; 21/8 is v1.x).
 * Radix traps focus and closes on Esc; ↑/↓ move within the visible rows; on
 * close, focus returns to the row (or the page `h1` when the row is not on the
 * page), except after the hand-off: its flag makes `onCloseAutoFocus` leave
 * focus to the panel's composer (OR-10). The scrim carries
 * `data-drawer-overlay` for WP11's geometry step.
 */
import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { ExportButton } from '@/components/trellis/ExportButton'
import { MaskedValue } from '@/components/trellis/MaskedValue'
import { RuleExplanation } from '@/components/trellis/RuleExplanation'
import { SeverityBadge } from '@/components/trellis/SeverityBadge'
import { SeverityLegend } from '@/components/trellis/SeverityLegend'
import { Button } from '@/components/ui/button'
import { Sheet, SheetBody, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableSkeletonRows } from '@/components/ui/table'
import type { ByRuleEntry, FindingEntry, FindingFilters, FindingsPage, TruncatedRule } from '@/lib/api/findings'
import type { Run } from '@/lib/api/runs'
import { useAssistantPanel } from '@/lib/assistant-panel'
import { Mono, count } from '@/lib/format'
import { ANA4_LEGEND } from '@/lib/mandatory-copy'
import { copyText } from '@/lib/provisional-copy'
import { findingsQuery, ruleQuery } from '@/lib/queries'
import { SEVERITY_ORDER, type Severity } from '@/lib/severity'

import { Pagination, segmentField } from './ByRuleTable'
import { ReadError } from './ReadError'
import { useCursorStack } from './useCursorStack'
import type { DrawerTarget } from './useResultsFilters'

export interface FindingDrawerProps {
  run: Run
  /** The visible (page-filtered) by-rule rows: the header source and the ↑/↓ list. */
  rows: ByRuleEntry[]
  /** The `['run', id, 'rule-counts']` page: `rule_counts` and `truncated_rules` (C3, C16). */
  ruleCounts: FindingsPage | undefined
  /** Truncated segments from the by-rule page, used when the rule-counts read failed. */
  truncated: TruncatedRule[]
  /** The by-rule page or the rule-counts read is still pending. */
  loading: boolean
  drawer: DrawerTarget | null
  onMove(step: 1 | -1): void
  onClose(): void
  /** The element focus returns to on close: the row, or the page `h1`. */
  originFor(drawer: DrawerTarget): HTMLElement | null
  /** `closeDrawer`'s search edit (`useResultsFilters().drawerCloseEdit`), applied by the hand-off (TR-6). */
  drawerCloseEdit(params: URLSearchParams): void
}

/** Render a value only when the API marked it available; anything else fails closed (TEN.10). */
function Value({ entry }: { entry: FindingEntry }) {
  if (entry.value_state === 'available' && entry.flagged_value !== null) return <Mono>{entry.flagged_value}</Mono>
  if (entry.value_state === 'not-located') return <span className="text-navy-600">{copyText('valueNotLocated')}</span>
  return <span className="text-navy-600">{copyText('valueWithheld')}</span>
}

function Records({ run, target }: { run: Run; target: DrawerTarget }) {
  const paging = useCursorStack(`${target.rule}|${target.severity ?? ''}`)
  const filters: FindingFilters = { rule_id: target.rule }
  if (target.severity !== null) filters.severity = target.severity
  if (paging.cursor !== undefined) filters.cursor = paging.cursor
  const query = useQuery(findingsQuery(run.run_id, filters, run.state === 'ready'))
  if (query.isError) return <ReadError error={query.error} onStartAgain={paging.reset} />
  const next = query.data?.next_cursor
  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">Line</TableHead>
            <TableHead>Record</TableHead>
            <TableHead>Field</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.data === undefined ? (
            <TableSkeletonRows columns={4} />
          ) : (
            query.data.entries.map((e, i) => (
              <TableRow key={`${e.line}|${i}`} dense>
                <TableCell numeric>{count(e.line)}</TableCell>
                <TableCell>
                  {e.record_ref === null ? (
                    <span className="text-navy-600">{copyText('noAccountNumber')}</span>
                  ) : (
                    <MaskedValue value={e.record_ref} />
                  )}
                </TableCell>
                <TableCell className="text-navy-700">{e.field ?? e.segment}</TableCell>
                <TableCell>
                  <Value entry={e} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Pagination
        className="flex items-center justify-end gap-2"
        paging={{
          canPrevious: paging.index > 0,
          canNext: next !== undefined,
          onPrevious: paging.previous,
          onNext: () => next !== undefined && paging.next(next),
        }}
      />
      <p className="text-caption text-navy-600">{copyText('formulaGuardNote')}</p>
    </div>
  )
}

function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      {title ? <h4 className="text-[16px] font-semibold leading-[22px] text-navy-950">{title}</h4> : null}
      {children}
    </section>
  )
}

type ContentProps = Omit<FindingDrawerProps, 'drawer' | 'onMove' | 'onClose' | 'originFor' | 'drawerCloseEdit'> & {
  target: DrawerTarget
  /** The hand-off; `total` is the rule's findings over every segment, when known. */
  onAskAssistant(total: number | undefined): void
}

function Content({ run, rows, ruleCounts, truncated, loading, target, onAskAssistant }: ContentProps) {
  const matches = rows.filter((r) => r.rule_id === target.rule && (target.severity === null || r.severity === target.severity))
  const rule = useQuery(ruleQuery(target.rule))
  const fromRow = matches.length > 0
  const single = matches.length === 1 ? matches[0] : undefined
  const description = fromRow ? (matches[0]?.description ?? null) : (rule.data?.description ?? null)
  const where = fromRow
    ? segmentField(matches[0]?.segment ?? null, matches[0]?.field ?? null)
    : rule.data
      ? segmentField(rule.data.segment, rule.data.field)
      : ''
  const findings = single ? single.count : fromRow ? undefined : ruleCounts?.rule_counts[target.rule]
  // The prefill's count (U7): the rule's total over its segments, defined for a two-segment drawer.
  const total = ruleCounts?.rule_counts[target.rule] ?? (fromRow ? matches.reduce((n, m) => n + m.count, 0) : undefined)

  // A segment with no listed rows sends no records request (DM-01M39PR43C8WYP2S7T8VYTYTZE).
  const segments = (ruleCounts?.truncated_rules ?? truncated).filter(
    (t) => t.rule_id === target.rule && (target.severity === null || t.severity === target.severity),
  )
  const decided = fromRow || !loading
  const nothingListed = fromRow ? matches.every((m) => m.in_window === 0) : segments.length > 0 && segments.every((s) => s.in_window === 0)

  // FR-020 A-L3: a bare `?rule=` off the page reads its severities from the truncated segments and from the
  // records table's own first page (the same query key, so no extra request).
  const offPage = !fromRow && target.severity === null
  const ready = run.state === 'ready'
  const firstRecords = useQuery({
    ...findingsQuery(run.run_id, { rule_id: target.rule }, ready),
    enabled: ready && offPage && decided && !nothingListed,
  })
  const severities: Severity[] = fromRow
    ? SEVERITY_ORDER.filter((s) => matches.some((m) => m.severity === s))
    : target.severity !== null
      ? [target.severity]
      : SEVERITY_ORDER.filter(
          (s) =>
            segments.some((t) => t.severity === s) ||
            (firstRecords.data?.entries ?? []).some((e) => e.rule_id === target.rule && e.severity === s),
        )

  return (
    <>
      <SheetHeader>
        <SheetTitle>
          <Mono>{target.rule}</Mono>
        </SheetTitle>
        {matches.length > 1
          ? matches.map((m) => (
              <span key={m.severity} data-drawer-segment="" className="inline-flex items-center gap-1">
                <SeverityBadge severity={m.severity} />
                <Mono className="text-body-sm text-navy-700">{count(m.count)}</Mono>
              </span>
            ))
          : severities.map((s) => <SeverityBadge key={s} severity={s} />)}
        <SheetClose asChild>
          <Button variant="ghost" size="icon" aria-label="Close">
            <X aria-hidden />
          </Button>
        </SheetClose>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-6">
        <Section>
          {description ? <p className="text-body text-navy-950">{description}</p> : null}
          <dl data-drawer-facts="" className="flex flex-wrap gap-x-6 gap-y-1 text-body-sm">
            {where ? (
              <div className="flex items-baseline gap-1.5">
                <dt className="text-navy-600">Segment · field</dt>
                <dd className="text-navy-950">{where}</dd>
              </div>
            ) : null}
            <div className="flex items-baseline gap-1.5">
              <dt className="text-navy-600">Rule set</dt>
              <dd>
                <Mono>{run.rule_set_version}</Mono>
              </dd>
            </div>
            {findings === undefined ? null : (
              <div className="flex items-baseline gap-1.5">
                <dt className="text-navy-600">{copyText('findingsColumn')}</dt>
                <dd>
                  <Mono>{count(findings)}</Mono>
                </dd>
              </div>
            )}
          </dl>
        </Section>
        <Section>
          {severities.map((s) => (
            <p key={s} data-ana4-line="" className="flex items-baseline gap-2 text-body text-navy-950">
              <SeverityBadge severity={s} />
              <span data-ana4-definition="">{ANA4_LEGEND[s]}</span>
            </p>
          ))}
          <p className="text-body-sm text-navy-600">{ANA4_LEGEND.attribution}</p>
          <SeverityLegend />
        </Section>
        <Section>
          <RuleExplanation ruleId={target.rule} />
        </Section>
        <Section title="Affected records">
          {!decided ? (
            <div data-records="">
              <Table>
                <TableBody>
                  <TableSkeletonRows columns={4} />
                </TableBody>
              </Table>
            </div>
          ) : nothingListed ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-body text-navy-950">{copyText('notInWindow')}</p>
              <ExportButton runId={run.run_id} />
            </div>
          ) : (
            <div data-records="">
              <Records run={run} target={target} />
            </div>
          )}
        </Section>
      </SheetBody>
      <SheetFooter>
        <Button asChild variant="secondary">
          <Link to={`/app/rules?rule=${encodeURIComponent(target.rule)}`}>View in rule catalog</Link>
        </Button>
        <Button variant="secondary" onClick={() => onAskAssistant(total)}>
          {copyText('assistantAskAboutRule')}
        </Button>
      </SheetFooter>
    </>
  )
}

export function FindingDrawer({ drawer, onMove, onClose, originFor, drawerCloseEdit, ...rest }: FindingDrawerProps) {
  // The drawer closes by URL, so the last target is kept for the closing render and the focus restore.
  const last = useRef<DrawerTarget | null>(null)
  if (drawer) last.current = drawer
  const target = drawer ?? last.current
  const { openPanel } = useAssistantPanel()
  // OR-10: set by the hand-off, read once by onCloseAutoFocus, so Radix does not return focus to the row.
  const handedOff = useRef(false)

  function askAssistant(rule: string, total: number | undefined) {
    handedOff.current = true
    // TR-6: the only navigation of the hand-off; it replaces the drawer's entry.
    openPanel({
      replace: true,
      rule,
      edit: drawerCloseEdit,
      ...(total === undefined ? {} : { prefill: { kind: 'rule' as const, ruleId: rule, count: total } }),
    })
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      onMove(event.key === 'ArrowDown' ? 1 : -1)
    }
  }

  return (
    <Sheet open={drawer !== null} onOpenChange={(open) => !open && onClose()}>
      {target ? (
        <SheetContent
          overlayProps={{ 'data-drawer-overlay': '' }}
          onKeyDown={onKeyDown}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            if (handedOff.current) {
              handedOff.current = false
              // The panel focuses its composer after its first frame; one already open is focused here, or its
              // Close when the composer is replaced or disabled (mission 8 review UI3, OR-10).
              ;(
                document.querySelector<HTMLElement>('[data-assistant-panel] textarea:not([disabled])') ??
                document.querySelector<HTMLElement>('[data-assistant-panel] [data-assistant-close]')
              )?.focus()
              return
            }
            const origin = last.current ? originFor(last.current) : null
            origin?.focus()
          }}
        >
          <Content {...rest} target={target} onAskAssistant={(total) => askAssistant(target.rule, total)} />
        </SheetContent>
      ) : null}
    </Sheet>
  )
}
